/**
 * The sales documents attached to a project.
 *
 * Read-only on purpose: everything here is owned by the sales section, and a
 * second place to edit an invoice is a second place for it to go wrong. The
 * number is a link into the document it belongs to.
 *
 * The two lists are drawn separately rather than through one row component,
 * because they are not the same record: the summary endpoint selects seven
 * fields for an invoice and five for an estimate. The old shared row declared
 * the three they have in common and threw the rest away, which is how an
 * operator ended up reading "1 200 EUR" on a project where 1 200 had already
 * been paid, and how a document in another currency was relabelled into euros.
 * Every figure below carries the currency the server stated for it.
 */
import { Link } from "react-router-dom";
import { Loader2, Receipt } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { ProjectSummary, ProjectSummaryEstimate, ProjectSummaryInvoice } from "../../../lib/projects";
import { Card, CardHead, EmptyState, fmtDate, listCard } from "../../ui";
import { StatusBadge } from "../sales/shared";
import { money } from "./lib";

/** Number, status, total, paid, issue date. */
const INVOICE_COLS = "minmax(0,1.6fr) 1fr .9fr .9fr .9fr";

/** Number, status, total. Narrower than the invoice grid on purpose: an
 *  estimate takes no payments and has no issue date of its own, so two dashed
 *  columns would be two columns claiming the record has fields it does not. */
const ESTIMATE_COLS = "minmax(0,1.6fr) 1fr .9fr";

function ListHead({ cols, labels }: { cols: string; labels: string[] }) {
  return (
    <div className="crm-list-head" style={{ ...listCard(cols), gridTemplateColumns: cols, paddingTop: 18 }}>
      {labels.map((label) => (
        <span key={label}>{label}</span>
      ))}
    </div>
  );
}

/** The document number as the row's link. The `::after` stretches the hit area
 *  over the whole row, which is how every other CRM list behaves. */
function NumberCell({ kind, id, number }: { kind: "invoices" | "estimates"; id: string; number: string }) {
  return (
    <Link
      to={`/sales/${kind}/${id}`}
      className="crm-prow-n crm-trunc block pl-3 after:absolute after:inset-0 after:rounded-full"
    >
      {number}
    </Link>
  );
}

function InvoiceRows({ rows }: { rows: ProjectSummaryInvoice[] }) {
  return (
    <div className="crm-rows pt-0" style={listCard(INVOICE_COLS)}>
      {rows.map((d) => (
        <div key={d.id} className="crm-list-row relative" style={{ gridTemplateColumns: INVOICE_COLS }}>
          <NumberCell kind="invoices" id={d.id} number={d.number} />
          <span>
            <StatusBadge status={d.status} />
          </span>
          <span className="crm-tnum text-[13.25px] font-bold">{money(d.totalCents, false, d.currency)}</span>
          {/* Nothing paid is a zero the operator needs to see, not an absence:
              it is the difference between "not asked for yet" and "asked for
              and unanswered". Muted rather than hidden. */}
          <span className={d.paidCents > 0 ? "crm-tnum text-[13.25px]" : "crm-mut crm-tnum text-[13.25px]"}>
            {money(d.paidCents, false, d.currency)}
          </span>
          <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(d.issueDate)}</span>
        </div>
      ))}
    </div>
  );
}

function EstimateRows({ rows }: { rows: ProjectSummaryEstimate[] }) {
  return (
    <div className="crm-rows pt-0" style={listCard(ESTIMATE_COLS)}>
      {rows.map((d) => (
        <div key={d.id} className="crm-list-row relative" style={{ gridTemplateColumns: ESTIMATE_COLS }}>
          <NumberCell kind="estimates" id={d.id} number={d.number} />
          <span>
            <StatusBadge status={d.status} />
          </span>
          <span className="crm-tnum text-[13.25px] font-bold">{money(d.totalCents, false, d.currency)}</span>
        </div>
      ))}
    </div>
  );
}

export function ProjectInvoices({ summary }: { summary: ProjectSummary | null }) {
  const t = useT();

  if (!summary) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  const { invoices, estimates } = summary;

  return (
    <div className="space-y-4">
      <Card>
        <CardHead title={t("projects.linkedInvoices")} />
        {invoices.length === 0 ? (
          <EmptyState icon={Receipt} title={t("projects.noInvoices")} />
        ) : (
          <>
            <ListHead
              cols={INVOICE_COLS}
              labels={[
                t("sales.col.number"),
                t("sales.col.status"),
                t("sales.col.total"),
                t("sales.col.paid"),
                t("sales.col.issueDate"),
              ]}
            />
            <InvoiceRows rows={invoices} />
          </>
        )}
      </Card>

      {estimates.length > 0 ? (
        <Card>
          <CardHead title={t("projects.linkedEstimates")} />
          <ListHead
            cols={ESTIMATE_COLS}
            labels={[t("sales.col.number"), t("sales.col.status"), t("sales.col.total")]}
          />
          <EstimateRows rows={estimates} />
        </Card>
      ) : null}
    </div>
  );
}
